import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, Truck, Save, Users } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Phase E (Feb 2026): Manager AM stock-load entry.
// Pick a salesman (or self), type loaded qty per product, submit.
export default function StockLoad() {
  const nav = useNavigate();
  const { t, lang, displayName } = useLang();
  const [people, setPeople] = useState([]); // salesmen + self
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [qty, setQty] = useState({}); // { product_id: "<num>" }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/users", { params: { role: "salesman" } }),
      api.get("/products"),
      api.get("/auth/me"),
    ]).then(([s, p, me]) => {
      const list = [
        { id: me.data.id, name: `${me.data.name} (${lang === "ta" ? "மேனேஜர்" : "Manager"})`, role: "manager" },
        ...(s.data || []),
      ];
      setPeople(list);
      setProducts(p.data || []);
      setSelectedId(me.data.id);
    });
  // eslint-disable-next-line
  }, []);

  const setProductQty = (id, raw) => {
    const cleaned = String(raw).replace(/\D/g, "").replace(/^0+(?=\d)/, "");
    setQty(prev => {
      const next = { ...prev };
      if (!cleaned) delete next[id]; else next[id] = cleaned;
      return next;
    });
  };

  const submit = async () => {
    if (!selectedId) return toast.error(lang === "ta" ? "ஒருவரை தேர்வு செய்" : "Pick a person");
    const items = Object.entries(qty)
      .map(([pid, q]) => ({ product_id: pid, loaded_qty: parseInt(q, 10) || 0 }))
      .filter(x => x.loaded_qty > 0);
    if (!items.length) return toast.error(lang === "ta" ? "எந்த பெட்டியும் சேர்க்கல" : "Add at least one product");
    setBusy(true);
    try {
      await api.post("/manager/stock/load", { salesman_id: selectedId, items });
      toast.success(lang === "ta" ? "ஸ்டாக் சேர்க்கப்பட்டது" : "Stock loaded");
      nav("/manager");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const totalBoxes = Object.values(qty).reduce((s, q) => s + (parseInt(q, 10) || 0), 0);

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-32 page-slide-in">
      <TopBar
        title={lang === "ta" ? "காலை ஸ்டாக் ஏற்றம்" : "AM Stock Loading"}
        onBack={() => nav("/manager")}
      />
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Person picker */}
        <div className="bg-white rounded-3xl p-5 soft-shadow space-y-3" data-testid="stockload-person-card">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#0055FF]" />
            <div className="heading-font font-bold text-base text-[#1D1D1F]">
              {lang === "ta" ? "எவருக்கு ஏற்றம்?" : "Loading For"}
            </div>
          </div>
          <select
            data-testid="stockload-person-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full bg-[#F5F5F7] rounded-2xl px-4 py-3.5 text-base font-semibold text-[#1D1D1F] focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 outline-none transition"
          >
            {people.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Per-product qty */}
        <div className="bg-white rounded-3xl p-5 soft-shadow space-y-3" data-testid="stockload-products-card">
          <div className="flex items-center gap-2 mb-1">
            <Boxes className="w-5 h-5 text-[#0055FF]" />
            <div className="heading-font font-bold text-base text-[#1D1D1F]">{t("products")}</div>
          </div>
          <div className="space-y-2">
            {products.map(p => (
              <div key={p.id} className="flex items-center gap-3" data-testid={`stockload-row-${p.sku}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold heading-font text-[#1D1D1F] truncate">{displayName(p)}</div>
                </div>
                <input
                  data-testid={`stockload-qty-${p.sku}`}
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="0"
                  value={qty[p.id] || ""}
                  onChange={(e) => setProductQty(p.id, e.target.value)}
                  onFocus={(e) => e.target.select()}
                  style={{ color: "#0A1A3F", WebkitTextFillColor: "#0A1A3F" }}
                  className="w-24 h-12 text-center text-xl font-bold rounded-2xl bg-[#F5F5F7] focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 outline-none transition placeholder:text-[#9CA3AF]"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {totalBoxes > 0 && (
        <div className="fixed bottom-0 left-0 right-0 glass border-t border-white/40 p-4">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
            <div>
              <div className="text-xs text-[#86868B] uppercase tracking-wider">{lang === "ta" ? "மொத்த பெட்டிகள்" : "Total Boxes"}</div>
              <div className="heading-font text-2xl font-bold text-[#1D1D1F]" data-testid="stockload-total">{totalBoxes}</div>
            </div>
            <button
              data-testid="stockload-submit"
              onClick={submit}
              disabled={busy}
              className="bg-[#34C759] hover:bg-[#2EAF4E] disabled:opacity-50 text-white rounded-2xl px-6 py-3 font-bold shadow-md flex items-center gap-2"
            >
              <Save className="w-5 h-5" /> {t("save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
